/// useTranslation not needed
import React, { ReactNode, Suspense, useEffect, useMemo, useState } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Toast, ToastBody, ToastHeader } from 'reactstrap'
import { subDays } from 'date-fns'
import Loading from '../components/Loading'
import { useGetAppConfigQuery } from '../store/apiSlice'
import { useTypedSelector } from '../store/store'
// @ts-ignore
import { useRegisterSW } from 'virtual:pwa-register/react'
// @ts-ignore
import { UploadProvider } from '../contexts/UploadContext'
import FourOhFour from '../components/FourOhFour'
import Changelog from '../components/Changelog'
import { useGetMyUserQuery } from '../store/userApiSlice'
import EditRequest from '../components/admin/EditRequest'
import { SyncedStoreProvider } from '../components/syncedstore/SyncedStoreContext'

const Login = React.lazy(
  () => import(/* webpackChunkName:'Login' */ '../components/auth/Login')
)
const Walks = React.lazy(
  () => import(/* webpackChunkName:'Walks' */ '../components/Walks')
)
const Signup = React.lazy(
  () => import(/* webpackChunkName:'Signup' */ '../components/auth/Signup')
)
const RequestReset = React.lazy(
  () =>
    import(
      /* webpackChunkName:'RequestReset' */ '../components/auth/RequestReset'
    )
)
const ResetPassword = React.lazy(
  () =>
    import(
      /* webpackChunkName:'ResetPassword' */ '../components/auth/ResetPassword'
    )
)
const Walk = React.lazy(
  () => import(/* webpackChunkName:'Walk' */ '../components/Walk')
)
const WalksStats = React.lazy(
  () => import(/* webpackChunkName:'Stats' */ '../components/WalksStats')
)
const WalkStats = React.lazy(
  () => import(/* webpackChunkName:'WalkStats' */ '../components/WalkStats')
)
const Account = React.lazy(
  () => import(/* webpackChunkName:'Account' */ '../components/Account')
)
const EditProfile = React.lazy(
  () => import(/* webpackChunkName:'EditProfile' */ '../components/EditProfile')
)
const Profiles = React.lazy(
  () => import(/* webpackChunkName:'Profiles' */ '../components/Profiles')
)
const PurchaseSubscription = React.lazy(
  () =>
    import(
      /* webpackChunkName:'PurchaseSubscription' */ '../components/subscription/PurchaseSubscription'
    )
)
const ThankYou = React.lazy(
  () =>
    import(
      /* webpackChunkName:'ThankYou' */ '../components/subscription/ThankYou'
    )
)
const Subscription = React.lazy(
  () =>
    import(
      /* webpackChunkName:'Subscription' */ '../components/subscription/Subscription'
    )
)
const Oops = React.lazy(
  () => import(/* webpackChunkName:'Oops' */ '../components/subscription/Oops')
)
const Users = React.lazy(
  () => import(/* webpackChunkName:'Users' */ '../components/admin/Users')
)
const AdminWalks = React.lazy(
  () => import(/* webpackChunkName:'AdminWalks' */ '../components/admin/Walks')
)
const Subscriptions = React.lazy(
  () =>
    import(
      /* webpackChunkName:'Subscriptions' */ '../components/admin/Subscriptions'
    )
)
const AdminProfiles = React.lazy(
  () =>
    import(
      /* webpackChunkName:'AdminProfiles' */ '../components/admin/Profiles'
    )
)
const AdminRequests = React.lazy(
  () =>
    import(
      /* webpackChunkName:'AdminRequests' */ '../components/admin/Requests'
    )
)
const User = React.lazy(
  () => import(/* webpackChunkName:'User' */ '../components/admin/User')
)

const App: React.FC = () => {
  const token = useTypedSelector(
    (state) => state.auth.token || localStorage.getItem('echoesToken')
  )
  const hasToken = useMemo(() => Boolean(token), [token])

  const { data: self, isFetching: isFetchingUser } = useGetMyUserQuery({})
  const isAdmin = useMemo(() => self?.role === 'admin', [self])
  const [registration, setRegistration] =
    useState<ServiceWorkerRegistration | null>(null)
  const lastFetchedAppConfigStr = useTypedSelector(
    (state) => state.creator.lastFetchedAppConfig
  )
  const lastFetchedAppConfig = useMemo(
    () => new Date(lastFetchedAppConfigStr),
    [lastFetchedAppConfigStr]
  )

  const { data: appConfig, refetch } = useGetAppConfigQuery(null)

  // replaced dynamically
  const buildDate = '__DATE__'
  // replaced dyanmicaly
  const reloadSW = '__RELOAD_SW__'

  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swUrl: string, r: ServiceWorkerRegistration | undefined) {
      if (!import.meta.env.PROD) console.info(`Service Worker at: ${swUrl}`)
      // @ts-expect-error just ignore
      if (reloadSW === 'true') {
        r &&
          setInterval(() => {
            console.log('Checking for sw update')
            r.update()
          }, 20000 /* 20s for testing purposes */)
      } else {
        // eslint-disable-next-line prefer-template
        if (!import.meta.env.PROD) console.info('SW Registered: ' + r)
      }
    },
    onRegisterError(error: any) {
      console.error('SW registration error', error)
    },
  })

  useEffect(() => {
    const getAppConfigIfNeeded = async () => {
      const oneDayAgo = subDays(new Date(), 1)
      if (token && oneDayAgo > lastFetchedAppConfig) {
        // only fetch config if it's stale
        refetch()
      }
    }
    setInterval(() => {
      getAppConfigIfNeeded()
    }, 60000)
    getAppConfigIfNeeded()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleClickAppUpdate = () => {
    setNeedRefresh(false)
    updateServiceWorker()
  }

  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/requestreset" element={<RequestReset />} />
        <Route path="/resetpassword" element={<ResetPassword />} />
        <Route path="/changelog" element={<Changelog />} />

        {/* Authenticated user routes */}
        <Route
          path="/walks"
          element={
            <RequireAuth redirectTo="/login" isAuthenticated={hasToken}>
              <Walks />
            </RequireAuth>
          }
        />
        <Route
          path="/walk/:collectionSlug/:tab?/:slug?/*"
          element={
            <RequireAuth redirectTo="/login" isAuthenticated={hasToken}>
              <UploadProvider>
                <Walk />
              </UploadProvider>
            </RequireAuth>
          }
        />
        <Route
          path="/stats/walks"
          element={
            <RequireAuth redirectTo="/login" isAuthenticated={hasToken}>
              <WalksStats />
            </RequireAuth>
          }
        />
        <Route
          path="/stats/walk/:slug"
          element={
            <RequireAuth redirectTo="/login" isAuthenticated={hasToken}>
              <WalkStats />
            </RequireAuth>
          }
        />
        <Route
          path="/account"
          element={
            <RequireAuth redirectTo="/login" isAuthenticated={hasToken}>
              <Account />
            </RequireAuth>
          }
        />
        <Route
          path="/profile/:slug"
          element={
            <RequireAuth redirectTo="/login" isAuthenticated={hasToken}>
              <UploadProvider>
                <EditProfile />
              </UploadProvider>
            </RequireAuth>
          }
        />
        <Route
          path="/profiles"
          element={
            <RequireAuth redirectTo="/login" isAuthenticated={hasToken}>
              <Profiles />
            </RequireAuth>
          }
        />
        <Route
          path="/subscription/purchase"
          element={
            <RequireAuth redirectTo="/login" isAuthenticated={hasToken}>
              <PurchaseSubscription />
            </RequireAuth>
          }
        />
        <Route
          path="/subscription/thankyou"
          element={
            <RequireAuth redirectTo="/login" isAuthenticated={hasToken}>
              <ThankYou />
            </RequireAuth>
          }
        />
        <Route
          path="/subscription/oops"
          element={
            <RequireAuth redirectTo="/login" isAuthenticated={hasToken}>
              <Oops />
            </RequireAuth>
          }
        />
        <Route
          path="/subscription/:slug"
          element={
            <RequireAuth redirectTo="/login" isAuthenticated={hasToken}>
              <Subscription />
            </RequireAuth>
          }
        />

        {/* Admin routes */}
        <Route
          path="/admin/*"
          element={
            isFetchingUser ? (
              <Loading />
            ) : (
              <RequireAuth
                redirectTo="/login"
                isAuthenticated={isAdmin && hasToken}
              >
                <Routes>
                  <Route path="users" element={<Users />} />
                  <Route path="user/:slug" element={<User />} />
                  <Route path="walks" element={<AdminWalks />} />
                  <Route path="requests" element={<AdminRequests />} />
                  <Route path="request/:id" element={<EditRequest />} />
                  <Route path="subscriptions" element={<Subscriptions />} />
                  <Route path="profiles" element={<AdminProfiles />} />
                </Routes>
              </RequireAuth>
            )
          }
        />

        <Route
          path="/collection/:slug"
          element={<Navigate to="/walk/:slug" />}
        />
        <Route path="/" element={<Navigate to="/walks" />} />
        <Route path="/*" element={<FourOhFour />} />
      </Routes>
      <Toast isOpen={needRefresh} className="fixed-bottom m-3">
        <ToastHeader>Update available</ToastHeader>
        <ToastBody>
          <p>
            There is an update available for the Creator. Refresh to get it.
          </p>
          <button type="button" className="btn btn-primary" onClick={() => handleClickAppUpdate()}>
            Refresh
          </button>
        </ToastBody>
      </Toast>
      {/* <Alert isOpen={appCachedOffline} className="fixed-bottom m-3">
        Creator cached offline
      </Alert> */}
    </Suspense>
  )
}

type RequireAuthProps = {
  children?: ReactNode
  redirectTo: string
  isAuthenticated: boolean
}

const RequireAuth: React.FC<RequireAuthProps> = ({
  children,
  redirectTo,
  isAuthenticated,
}) => {
  const location = useLocation()

  if (isAuthenticated) {
    return <>{children}</>
  }

  const referrer = location.pathname + location.search + location.hash
  return <Navigate to={redirectTo} state={{ referrer }} replace />
}

// App.whyDidYouRender = true

export default App
